import { defineStore } from 'pinia'
import { auth } from '@/firebase/init'
import {
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
} from 'firebase/auth'
import type { User } from 'firebase/auth'
import router from '@/router'
import { useUserStore } from './userStore'
import { useAccountStore } from '@/stores/accountStore'
import { useInviteStore } from '@/stores/inviteStore'
import { useAppLockStore } from '@/stores/appLockStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import { useDropPointStore } from '@/stores/dropPointStore'
import { useClosedDayStore } from '@/stores/closedDayStore'
import { useOrderStore } from '@/stores/orderStore'
import { useCustomerReportStore } from '@/stores/customerReportStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { useAnalysisTestStore } from '@/stores/analysisTestStore'
import { useTextListStore } from '@/stores/textListStore'
import { useAnalysisResultStore } from '@/stores/analysisResultStore'
import { useReportTemplateStore } from '@/stores/reportTemplateStore'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'
import { useProjectStore } from '@/stores/projectStore'
import { usePlaceOrderStore } from '@/stores/placeOrderStore'
import {
  CodeAuthGatewayError,
  identify as identifyGateway,
  sendSmsCode as sendSmsCodeGateway,
  verifySmsCode as verifySmsCodeGateway,
  sendEmailCode as sendEmailCodeGateway,
  verifyEmailCode as verifyEmailCodeGateway,
  type TIdentifyChannels,
} from '@/globals/typescript/utils/codeAuthGateway'

export type TLoginStep =
  | 'identify'
  | 'choose-channel'
  | 'enter-sms-code'
  | 'enter-email-code'

// Matches the backend's per-click cooldown (otpRateLimiter.ts) — used only
// to show an optimistic countdown right after a successful send, since a
// success response carries no retryAfterSeconds of its own. A real
// TOO_MANY_REQUESTS response's retryAfterSeconds always wins once it arrives.
const RESEND_COOLDOWN_SECONDS = 30

// Sends a code and returns when the user may ask for another one. A send rejected
// by the backend's short per-click cooldown (retryAfterSeconds <= the cooldown)
// means a code went out seconds ago — e.g. the user stepped back to the channel
// choice and picked the same channel again — so that code is still the one to
// enter, and the rejection's own retryAfterSeconds is the remaining wait. The
// longer 15-minute window limit is deliberately not treated this way: the last
// code may be old or expired by then, so that error still propagates.
async function sendCodeResumingCooldown(send: () => Promise<void>): Promise<number> {
  try {
    await send()
    return Date.now() + RESEND_COOLDOWN_SECONDS * 1000
  } catch (error) {
    if (
      error instanceof CodeAuthGatewayError &&
      error.message === 'too-many-requests' &&
      error.retryAfterSeconds !== undefined &&
      error.retryAfterSeconds <= RESEND_COOLDOWN_SECONDS
    ) {
      return Date.now() + error.retryAfterSeconds * 1000
    }
    throw error
  }
}

// Shared by every login flow below (initial session restore, code-based completion): loads the user doc (which re-applies
// their own saved language, if any — see userStore.getCurrentUser), then the
// lab/customer account data, then resolves and persists the account default
// language for a genuine first login or a saved language the account no longer allows. See "Per-Lab & Per-Customer Language
// Selection", Phase 3.
//
// Resolves `false` when the signed-in account has no user document, in which case nothing else is
// loaded and the caller signs the session out.
async function loadUserAndAccountData(uid: string): Promise<boolean> {
  const userStore = useUserStore()
  const accountStore = useAccountStore()

  const currentUser = await userStore.getCurrentUser(uid)
  if (!currentUser) return false

  await Promise.all([
    accountStore.getLabAccountFromUser(),
    accountStore.getCustomerAccount(),
  ])

  await userStore.reconcileLanguageWithAccount(
    accountStore.labAccount,
    accountStore.customerAccount
  )

  return true
}

// `storage.rules` lets a customer read their own files only through the `customerAccountID`
// claim on the sign-in token, which is minted at login. A session restored from before that
// claim existed never gets it — a token refresh keeps the claims it already had — so every
// report and invoice download answers 403 until the user signs in again.
//
// An insurance user (MIL-402) has no `customerAccountID` at all; their session carries a
// `customer` claim of type `insurance` instead, and that is what keeps it. A business session
// still needs `customerAccountID` — a `customer` claim alone does not satisfy the Storage rules
// it is checked for.
export function isMissingCustomerClaim(claims: Record<string, unknown>): boolean {
  const { customerAccountID, customer } = claims
  if (typeof customerAccountID === 'string' && customerAccountID !== '') {
    return false
  }
  return !isInsuranceCustomerClaim(customer)
}

function isInsuranceCustomerClaim(customer: unknown): boolean {
  if (typeof customer !== 'object' || customer === null) return false
  const { type, id } = customer as { type?: unknown; id?: unknown }
  return type === 'insurance' && typeof id === 'string' && id !== ''
}

// Returns false only when the token was read and the claim is definitely absent; if the token
// cannot be read (offline with an expired token) the session is kept, since signing out an offline
// user would lock them out of the app for a reason they cannot fix.
async function isMissingCustomerAccountClaim(user: User): Promise<boolean> {
  try {
    const { claims } = await user.getIdTokenResult()
    return isMissingCustomerClaim(claims)
  } catch {
    return false
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as User | null,
    ready: false,

    login: {
      step: 'identify' as TLoginStep,
      email: '',
      channels: null as TIdentifyChannels | null,
      resendAvailableAt: null as number | null,
    },
  }),

  actions: {
    init(): Promise<void> {
      if (this.ready) return Promise.resolve()

      return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, async (restoredUser) => {
          let user = restoredUser

          if (user && (await isMissingCustomerAccountClaim(user))) {
            await this.signOutWithoutRedirect()
            user = null
          }

          this.user = user

          // A restored login whose user document is gone must not leave the app waiting forever
          // for a user that will never load: sign it out so the login page shows.
          if (user && !(await loadUserAndAccountData(user.uid))) {
            await this.signOutWithoutRedirect()
          }

          this.ready = true
          resolve()
          unsubscribe()
        })
      })
    },

    updateUser(user: User | null) {
      this.user = user
    },

    async signOutWithoutRedirect(): Promise<void> {
      const userStore = useUserStore()
      const accountStore = useAccountStore()
      const inviteStore = useInviteStore()
      const appLockStore = useAppLockStore()
      const analysisStore = useAnalysisStore()
      const sampleAreaStore = useSampleAreaStore()
      const turnaroundTimeStore = useTurnaroundTimeStore()
      const dropPointStore = useDropPointStore()
      const closedDayStore = useClosedDayStore()
      const orderStore = useOrderStore()
      const customerReportStore = useCustomerReportStore()
      const insuranceCustomerAccountStore = useInsuranceCustomerAccountStore()
      const analysisTestStore = useAnalysisTestStore()
      const textListStore = useTextListStore()
      const analysisResultStore = useAnalysisResultStore()
      const reportTemplateStore = useReportTemplateStore()
      const reportDescriptionItemStore = useReportDescriptionItemStore()
      const projectStore = useProjectStore()
      const placeOrderStore = usePlaceOrderStore()

      await signOut(auth)

      userStore.resetStore()
      accountStore.resetStore()
      inviteStore.resetStore()
      appLockStore.resetStore()
      analysisStore.resetStore()
      sampleAreaStore.resetStore()
      turnaroundTimeStore.resetStore()
      dropPointStore.resetStore()
      closedDayStore.resetStore()
      // Both hold live Firestore subscriptions scoped to the logged-in
      // user/lab (MIL-334 Phase 3) — must be torn down on logout or a
      // subsequent user could see the previous user's data.
      orderStore.resetStore()
      customerReportStore.resetStore()
      insuranceCustomerAccountStore.resetStore()
      // MIL-338 Phase 2 — same reasoning: lab catalog data (analysisTestStore/
      // textListStore) is lab-scoped, and analysisResultStore holds live
      // per-order subscriptions.
      analysisTestStore.resetStore()
      textListStore.resetStore()
      analysisResultStore.resetStore()
      // MIL-338 Phase 3 — same reasoning as the two catalog stores above.
      reportTemplateStore.resetStore()
      // Lab-scoped live catalog used by OrderResultsView — same reasoning again: the
      // listener must be torn down and the items cleared, or the next user (possibly of
      // another lab) inherits the previous lab's description items.
      reportDescriptionItemStore.resetStore()
      // "Lab Customer — Projects, Floor Plans & Samples" plan, Phase 6 — same
      // reasoning as orderStore/analysisResultStore: holds live per-user
      // Firestore subscriptions (Projects, FloorPlans).
      projectStore.resetStore()
      // Mirrored to sessionStorage — must be cleared on logout or the next
      // user in this tab would inherit the previous user's TAT selection.
      placeOrderStore.resetStore()

      this.user = null
    },

    async logout(): Promise<void> {
      await this.signOutWithoutRedirect()
      await router.push({ name: 'Home' })
    },

    // Step 1: identify by email, then advance to Step 2 (channel choice).
    // Throws (INVALID_CREDENTIALS) on an unknown/deactivated email — the view
    // shows the ambiguous error inline and stays on Step 1.
    async identifyEmail(email: string): Promise<void> {
      const channels = await identifyGateway(email)
      this.login.email = email
      this.login.channels = channels
      this.login.step = 'choose-channel'
    },

    // Step 2 → 3B: sends an SMS code and advances to code entry.
    async chooseSmsCode(): Promise<void> {
      const resendAvailableAt = await sendCodeResumingCooldown(() => sendSmsCodeGateway(this.login.email))
      this.login.step = 'enter-sms-code'
      this.login.resendAvailableAt = resendAvailableAt
    },

    async resendSmsCode(): Promise<void> {
      await sendSmsCodeGateway(this.login.email)
      this.login.resendAvailableAt = Date.now() + RESEND_COOLDOWN_SECONDS * 1000
    },

    // Verifies the entered SMS code, exchanging the resulting Firebase custom
    // token for a real session the same way completeEmailLinkSignIn consumes
    // an email-link credential. Throws on an invalid/expired/rate-limited
    // code; the view translates the thrown error's message (the response's
    // error code) into a user-facing message, same as every other auth form.
    async verifySmsCode(code: string): Promise<void> {
      const customToken = await verifySmsCodeGateway(this.login.email, code)
      await this.completeCodeSignIn(customToken)
    },

    // Step 2 → 3C: sends an email code and advances to code entry.
    async chooseEmailCode(): Promise<void> {
      const resendAvailableAt = await sendCodeResumingCooldown(() => sendEmailCodeGateway(this.login.email))
      this.login.step = 'enter-email-code'
      this.login.resendAvailableAt = resendAvailableAt
    },

    async resendEmailCode(): Promise<void> {
      await sendEmailCodeGateway(this.login.email)
      this.login.resendAvailableAt = Date.now() + RESEND_COOLDOWN_SECONDS * 1000
    },

    async verifyEmailCode(code: string): Promise<void> {
      const customToken = await verifyEmailCodeGateway(this.login.email, code)
      await this.completeCodeSignIn(customToken)
    },

    // Shared by both code channels: exchange the custom token for a real
    // session, same shape as completeEmailLinkSignIn.
    async completeCodeSignIn(customToken: string): Promise<void> {
      const credential = await signInWithCustomToken(auth, customToken)
      this.user = credential.user

      if (!(await loadUserAndAccountData(credential.user.uid))) {
        await this.signOutWithoutRedirect()
        throw new Error('user-not-found')
      }
    },

    // Step 2's "back" action from a code-entry screen — returns to channel
    // choice with the same identified email, clearing any pending code state.
    backToChannelChoice(): void {
      this.login.step = 'choose-channel'
      this.login.resendAvailableAt = null
    },

    // Full reset — used on unmount and Step 2's "change email" back-action.
    resetLogin(): void {
      this.login = {
        step: 'identify',
        email: '',
        channels: null,
        resendAvailableAt: null,
      }
    },
  },

  getters: {
    isLoggedIn: (state) => !!state.user,
  },
})
